import SoundManager from "../../../../../shared/lib/html5-software-library/src/ranj/audio/SoundManager";
import LanguagePack from "../../../../../shared/lib/html5-software-library/src/ranj/language/LanguagePack";
import StageManager from "../../../../../shared/lib/html5-software-library/src/ranj/performance/StageManager";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import AbstractContextObject from "../../../../../shared/src/ranj/app/core/AbstractContextObject";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";
import NavigateEvent from "../../../../../shared/src/ranj/app/navigation/NavigateEvent";
import SkinFactory from "../../../../../shared/src/ranj/app/ui/SkinFactory";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import GameModel from "../model/GameModel";
import TutorialOverlayMediatorEvent from "../model/TutorialOverlayMediatorEvent";
import TutorialPlayerEvent from "../model/TutorialPlayerEvent";
import TutorialStep from "../model/TutorialStep";
import RoadView from "../view/RoadView";
import HudView from "../view/HudView";

export default class TutorialPlayer extends AbstractContextObject {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     * @param {AppModel} appModel 
     * @param {GameModel} gameModel 
     * @param {Object} gameConfig - parsed json (game.json)
     * @param {StageManager} stageManager
     */
    constructor(dispatcher, languagePack, soundManager, appModel, gameModel, gameConfig, stageManager) {
        super(dispatcher, languagePack, soundManager, appModel);

        this._gameModel = gameModel;
        this._gameConfig = gameConfig;
        this._stageManager = stageManager;

        /** @type {RoadView} */
        this._roadView = null;
        /** @type {HudView} */
        this._hudView = null;

        /** @type {Array<TutorialStep>} */
        this._steps = [];
        this._currentStep = -1;

        this._tutorialMoveCount = 0;

        this._mainStage = this._stageManager.getStages()["mainGameCanvas"];

        this._nextStepBound = this.nextStep.bind(this);
        this._replayBound = this.replay.bind(this);
    }

    initialize() {
        this._initializeSteps();
    }

    reset() {
        this._roadView = null;
        this._hudView = null;

        this._tutorialMoveCount = 0;

        // ensure all listeners are removed from the tutorial steps
        for (let i = 0; i < this._steps.length; i++) {
            const step = this._steps[i];
            step.removeEventListener(TutorialStep.COMPLETED, this._nextStepBound);
            step.destroy();
        }

        this._steps = [];
        this._currentStep = -1;
    }

    destroy() {
        this.reset();

        super.destroy();
    }

    set roadView(value) {
        this._roadView = value;
    }

    set hudView(value) {
        this._hudView = value;
    }

    start() {
        this._gameModel.startTutorial();
        this._gameModel.overrideSpeed = 0;
        this._gameModel.offset = 4830; // number found by logging GameModel.offset at start of the game
        this._gameModel.wrapOffsetAtTo(9120, 8040); // keep this dividable by 1080 so that the tutorial road dividers loop nicely
        this._hudView.enableTutorialMode();

        // Ensure that the car is on the third lane (from the left) after replay
        this._roadView.setLane(2, false);

        const animationDelay = this._gameConfig.getValue("tutorial.delay");
        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(animationDelay, animationDuration, createjs.Ease.quadIn, this._nextStepBound);

        this._tutorialOverlayPopup = SkinFactory.getSkin(SkinKeys.TUTORIAL_OVERLAY_SKIN);
        const commandEvent = new NavigateEvent(NavigateEvent.POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_IN, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    replay() {
        // unregister listeners from gatekeeper step
        this.unregisterEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
        this.unregisterEventListener(TutorialOverlayMediatorEvent.CONTINUE_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);

        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(0, animationDuration, createjs.Ease.quadIn, () => {
            this.dispatch(TutorialPlayerEvent.REPLAY);
        });

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_OUT, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    complete() {
        this._roadView.enableAllLanes();
        this._gameModel.removeSpeedOverride();
        this._gameModel.removeOffsetWrapping();

        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(0, animationDuration, createjs.Ease.quadIn, () => {
            this._gameModel.elapsedTime = 0;
            this._hudView.disableTutorialMode();

            this.dispatch(TutorialPlayerEvent.COMPLETED);

            this.destroy();
        });

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_OUT, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    nextStep() {
        if (this._currentStep + 1 >= this._steps.length) {
            this.complete();
            return;
        }

        if (this._currentStep >= 0) {
            const previousStep = this._steps[this._currentStep];
            previousStep.removeEventListener(TutorialStep.COMPLETED, this._nextStepBound);

            const completeStepEvent = new TutorialPlayerEvent(TutorialPlayerEvent.COMPLETE_STEP, { stepIndex: this._currentStep });
            this.dispatch(completeStepEvent.type, completeStepEvent);
        }

        const nextStep = this._steps[this._currentStep + 1];
        nextStep.addEventListener(TutorialStep.COMPLETED, this._nextStepBound);

        const enterFunc = () => {
            nextStep.enter();

            this._currentStep++;

            const nextStepEvent = new TutorialPlayerEvent(TutorialPlayerEvent.NEXT_STEP, { stepIndex: this._currentStep });
            this.dispatch(nextStepEvent.type, nextStepEvent);
        };

        if (nextStep.delay) {
            setTimeout(enterFunc, nextStep.delay);
        } else {
            enterFunc();
        }

        this._timeAtWhichStepBecameAvailable = new Date();
    }

    _initializeSteps() {
        const introStep = new TutorialStep();
        introStep.onEnter = () => {
            this._tutorialIntroStepCompleteBound = introStep.complete.bind(introStep);

            this._gameModel.overrideSpeed = 0;
            this._roadView.hideCarGas();

            this.registerEventListener(TutorialOverlayMediatorEvent.CONTINUE_BUTTON_CLICKED, this._tutorialIntroStepCompleteBound);
        };
        introStep.onComplete = () => {
            this.unregisterEventListener(TutorialOverlayMediatorEvent.CONTINUE_BUTTON_CLICKED, this._tutorialIntroStepCompleteBound);
        };

        this._steps.push(introStep);


        this._steps.push(this._constructNonInteractiveTutorialStep());


        const learnToMoveStep = new TutorialStep();
        learnToMoveStep.onEnter = () => {
            this._roadView.disableAllLanes();
            this._roadView.enableLaneIndex(0);
            this._roadView.enableInteraction(true);

            this._gameModel.overrideSpeed = 14.4; // speed = 2
            this._hudView.animateSpeedMeterTo(2);
            this._roadView.showCarGas();

            this._tutorialLearnToMoveStepCompleteBound = learnToMoveStep.complete.bind(learnToMoveStep);
            this._roadView.addEventListener(RoadView.JUMP, this._tutorialLearnToMoveStepCompleteBound);
        };
        learnToMoveStep.onComplete = () => {
            this._roadView.removeEventListener(RoadView.JUMP, this._tutorialLearnToMoveStepCompleteBound);

            this._roadView.enableAllLanes();
            this._roadView.enableInteraction(false);

            this._gameModel.overrideSpeed = 9.6; // speed = 1
            this._hudView.animateSpeedMeterTo(1);
            this._roadView.showCarGas();
        };

        this._steps.push(learnToMoveStep);


        this._steps.push(this._constructNonInteractiveTutorialStep());


        const learnSpeedWithMoveStep = new TutorialStep();
        learnSpeedWithMoveStep.onEnter = () => {
            this._roadView.disableAllLanes();
            this._roadView.enableLaneIndex(1);
            this._roadView.enableInteraction(true);

            this._tutorialLearnSpeedWithMoveStepCompleteBound = learnSpeedWithMoveStep.complete.bind(learnSpeedWithMoveStep);
            this._roadView.addEventListener(RoadView.JUMP, this._tutorialLearnSpeedWithMoveStepCompleteBound);
        };
        learnSpeedWithMoveStep.onComplete = () => {
            this._roadView.removeEventListener(RoadView.JUMP, this._tutorialLearnSpeedWithMoveStepCompleteBound);

            this._roadView.enableAllLanes();
            this._roadView.enableInteraction(false);

            this._gameModel.overrideSpeed = 24; // speed = 4
            this._hudView.animateSpeedMeterTo(4);
            this._roadView.showCarGas();
        };

        this._steps.push(learnSpeedWithMoveStep);


        const speedMeterChangedStep = new TutorialStep();
        speedMeterChangedStep.delay = 1500;
        speedMeterChangedStep.onEnter = () => {
            this._tutorialSpeedMeterChangedStepCompleteBound = speedMeterChangedStep.complete.bind(speedMeterChangedStep);

            this._speedMeterChangedStepAntiSkip = () => {
                const at = new Date();
                if (this._timeAtWhichStepBecameAvailable < at) {
                    this._mainStage.addEventListener("pressup", this._tutorialSpeedMeterChangedStepCompleteBound);
                }
            }

            this._mainStage.addEventListener('mousedown', this._speedMeterChangedStepAntiSkip);
        };
        speedMeterChangedStep.onComplete = () => {
            this._mainStage.removeEventListener('mousedown', this._speedMeterChangedStepAntiSkip);
            this._mainStage.removeEventListener("pressup", this._tutorialSpeedMeterChangedStepCompleteBound);

            this._gameModel.overrideSpeed = 24; // speed = 4
            this._roadView.showCarGas();
        };
        this._steps.push(speedMeterChangedStep);


        // Move to start step
        const moveToStartStep = new TutorialStep();
        moveToStartStep.onEnter = () => {
            const tutorialMoveToStartStepCompleteBound = moveToStartStep.complete.bind(moveToStartStep);

            this._gameModel.removeOffsetWrapping();

            let gameModel = this._gameModel;
            (function waitForCarToReachStart() {
                if (gameModel.offset >= 10199) { // 10199 is a 'magic number' found by logging the gameModel.offset and seeing when the car is around the start
                    tutorialMoveToStartStepCompleteBound();
                }
                setTimeout(waitForCarToReachStart, 30);
            })();
        };
        moveToStartStep.onComplete = () => {
            this._hudView.animateSpeedMeterTo(0);
        };

        this._steps.push(moveToStartStep);


        const gateKeeperStep = new TutorialStep();
        gateKeeperStep.onEnter = () => {
            this._tutorialGatekeeperStepCompleteBound = gateKeeperStep.complete.bind(gateKeeperStep);

            this.registerEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
            this.registerEventListener(TutorialOverlayMediatorEvent.CONTINUE_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);
        };
        gateKeeperStep.onComplete = () => {
            this.unregisterEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
            this.unregisterEventListener(TutorialOverlayMediatorEvent.CONTINUE_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);
        };

        this._steps.push(gateKeeperStep);
    }

    _constructNonInteractiveTutorialStep() {
        const step = new TutorialStep();
        step.onEnter = () => {
            this._tutorialStepCompleteBound = step.complete.bind(step);

            this._nonInteractiveStepAntiSkip = () => {
                const at = new Date();
                if (this._timeAtWhichStepBecameAvailable < at) {
                    this._mainStage.addEventListener("pressup", this._tutorialStepCompleteBound);
                }
            }

            this._mainStage.addEventListener('mousedown', this._nonInteractiveStepAntiSkip);
        };
        step.onComplete = () => {
            this._mainStage.removeEventListener("mousedown", this._nonInteractiveStepAntiSkip);
            this._mainStage.removeEventListener("pressup", this._tutorialStepCompleteBound);
        };

        return step;
    }
}